import * as z from "zod";

export const LLMProviderSchema = z.enum([
    "OpenAI",
    "Anthropic",
    "Gemini",
    "Grok",
    "OpenAI-compatible",
    "OpenRouter",
    "Mock",
]);
/**
 * Parameters for controlling LLM text generation behavior.
 * All parameters are optional and can be adjusted based on the specific use case.
 * Refer to the documentation of the specific LLM provider for details on supported parameters.
 */
export const LLMGenerationParametersSchema = z.object({
    /**
     * Controls the randomness of the model's output.
     * Usually between 0 and 2, but can vary by model.
     */
    temperature: z.number().optional(),
    /**
     * Maximum number of input tokens to consider from the prompt.
     * Keep in mind that this is not exact, since it will calculate tokens based on
     * the tokenizator of the specific model being used.
     */
    maxInputTokens: z.number().int().min(1).optional(),
    /**
     * Maximum number of tokens to generate in the response.
     */
    maxOutputTokens: z.number().int().min(1).optional(),
    /**
     * Controls repetition in the generated text.
     */
    frequencyPenalty: z.number().min(-2).max(2).optional(),
    /**
     * Controls presence of new topics in the generated text.
     */
    presencePenalty: z.number().min(-2).max(2).optional(),
    /**
     * Top-p (nucleus) sampling parameter.
     */
    topP: z.number().min(0).max(1).optional(),
    /**
     * Top-k sampling parameter.
     */
    topK: z.number().int().min(0).optional(),
    /**
     * Thinking level for reasoning models.
     * Usually token count, or textual description like "low", "medium", "high".
     */
    thinkingLevel: z.union([z.number().int().min(0), z.string()]).optional(),
});

const BaseLLMConfigSchema = z.object({
    /**
     * Unique identifier for the LLM configuration.
     */
    id: z.string().default(() => `LLMconfig-${crypto.randomUUID()}`),
    /**
     * Human-readable name for the LLM configuration.
     * Just for easier identification.
     */
    name: z.string().default("New Model"),
    /**
     * API key for accessing the LLM service.
     * Optional, as some providers may not require it(self-hosted or mock providers).
     */
    apiKey: z.string().optional(),
    /**
     * Base URL for the LLM service API.
     * Overrides default endpoint for custom or self-hosted providers.
     */
    baseURL: z.string().optional(),
    /**
     * Specific model name or identifier to use with the provider.
     * Optional, as some configurations may use default models.
     */
    model: z.string().optional(),
    /**
     * Configuration parameters for text generation behavior.
     */
    generationParameters: LLMGenerationParametersSchema.default({}),
    /**
     * Whether this LLM configuration is enabled for use.
     */
    enabled: z.boolean().default(true),
});

export const OpenAILLMConfigSchema = BaseLLMConfigSchema.extend({
    provider: z.literal("OpenAI"),
});

export const OpenAICompatibleLLMConfigSchema = BaseLLMConfigSchema.extend({
    provider: z.literal("OpenAI-compatible"),
});

export const AnthropicLLMConfigSchema = BaseLLMConfigSchema.extend({
    provider: z.literal("Anthropic"),
});

export const GeminiSafetySettingSchema = z.enum([
    "HARM_CATEGORY_UNSPECIFIED",
    "HARM_CATEGORY_HATE_SPEECH",
    "HARM_CATEGORY_SEXUALLY_EXPLICIT",
    "HARM_CATEGORY_HARASSMENT",
    "HARM_CATEGORY_DANGEROUS_CONTENT",
    "HARM_CATEGORY_CIVIC_INTEGRITY",
]);

export const GeminiHarmBlockThresholdSchema = z.enum([
    "HARM_BLOCK_THRESHOLD_UNSPECIFIED",
    "BLOCK_LOW_AND_ABOVE",
    "BLOCK_MEDIUM_AND_ABOVE",
    "BLOCK_ONLY_HIGH",
    "BLOCK_NONE",
]);

export const GeminiLLMConfigSchema = BaseLLMConfigSchema.extend({
    provider: z.literal("Gemini"),
    safetySettings: z
        .array(
            z.object({
                category: GeminiSafetySettingSchema,
                threshold: GeminiHarmBlockThresholdSchema,
            })
        )
        .optional(),
});

export const OpenRouterLLMConfigSchema = BaseLLMConfigSchema.extend({
    provider: z.literal("OpenRouter"),
});

export const GrokLLMConfigSchema = BaseLLMConfigSchema.extend({
    provider: z.literal("Grok"),
});

export const MockLLMConfigSchema = BaseLLMConfigSchema.extend({
    provider: z.literal("Mock"),
    mockDelay: z.number().optional(),
    responses: z.array(z.string()).optional(),
});
export const LLMConfigSchema = z.discriminatedUnion("provider", [
    OpenAILLMConfigSchema,
    OpenAICompatibleLLMConfigSchema,
    AnthropicLLMConfigSchema,
    GeminiLLMConfigSchema,
    GrokLLMConfigSchema,
    OpenRouterLLMConfigSchema,
    MockLLMConfigSchema,
] as const satisfies Array<
    z.ZodObject<{
        provider: z.ZodLiteral<string>;
    }>
>);

/**
 * Configuration for system prompts to guide assistant behavior.
 * ChatML format. All prompts are required.
 *
 * Sections can be individually enabled/disabled and reordered via
 * `promptSections`. When a section is disabled, it is omitted from
 * the assembled system prompt sent to the LLM.
 */
export const PromptConfigSchema = z
    .object({
        /**
         * The general prompt to guide the behavior of the assistant.
         */
        generationPrompt: z.string().default("You are a helpful assistant."),
        /**
         * Controls which prompt sections are included and their order
         * in the assembled system prompt.
         * Each entry: { key, enabled }.
         * Missing keys default to enabled.
         */
        promptSections: z
            .array(
                z.object({
                    key: z.enum(["system", "character", "persona", "lore"]),
                    enabled: z.boolean(),
                })
            )
            .optional(),
    })
    .default({
        generationPrompt: "You are a helpful assistant.",
    });

export const AdvancedConfigSchema = z
    .object({
        /**
         * Enables debug mode for more verbose logging and diagnostics.
         * Currently unused.
         */
        debug: z.boolean().default(false),
        /**
         * Enables experimental features that are still in development.
         * Use with caution as these features may be unstable.
         * Currently unused.
         */
        experimental: z.boolean().default(false),
    })
    .prefault({});

export const SettingsSchema = z
    .object({
        /**
         * Global font size in pixels.
         * Default is 16px (Medium).
         */
        fontSize: z.number().min(10).max(32).default(16),
        /**
         * Global font family.
         * Default is "Noto Sans KR".
         */
        fontFamily: z.string().default("Noto Sans KR"),
        /**
         * Theme preference for the application interface.
         */
        theme: z.enum(["light", "dark", "system"]).default("system"),
        /**
         * Identifier of the active persona.
         * Nullable means no persona is selected.
         * Keep in mind that user can't send messages without selecting a persona first.
         */
        activePersonaId: z.string().nullable().default(null),
        /**
         * List of configured LLMs available for use.
         * Can be empty if no LLMs are set up.
         */
        llmConfigs: z.array(LLMConfigSchema).default([]),
        /**
         * ID of the currently active LLM configuration.
         * Used by chatStore to determine which provider to use.
         * Nullable means no config is active.
         */
        activeLLMConfigId: z.string().nullable().default(null),
        /**
         * Prompt configuration to guide assistant behavior.
         */
        prompt: PromptConfigSchema,
        /**
         * Advanced settings for the application.
         */
        advanced: AdvancedConfigSchema,
    })
    .prefault({});

/**
 * Represents application settings.
 */
export type Settings = z.infer<typeof SettingsSchema>;
export type LLMConfig = z.infer<typeof LLMConfigSchema>;
export type LLMProvider = z.infer<typeof LLMProviderSchema>;
export type LLMGenerationParameters = z.infer<typeof LLMGenerationParametersSchema>;
export type PromptConfig = z.infer<typeof PromptConfigSchema>;
export type AdvancedConfig = z.infer<typeof AdvancedConfigSchema>;

/**
 * Known image generation diffusion models.
 */
export const ImageGenModelSchema = z.enum([
    "nai-diffusion-4",
    "nai-diffusion-3",
    "nai-diffusion-2",
    "nai-diffusion-1",
    "anime-full",
    "anime-curated",
    "furry-v3",
    "furry-v2",
    "furry-v1",
]);

/**
 * Configuration for image generation.
 * Stored as a settings block alongside LLM configs.
 */
export const ImageGenConfigSchema = z.object({
    /** API key for image generation service authentication. */
    apiKey: z.string().min(1, "API key is required"),
    /** Model name to use for generation. */
    model: ImageGenModelSchema.default("nai-diffusion-4"),
    /** Image width in pixels (64–2048, multiples of 64). */
    width: z.number().int().min(64).max(2048).default(1024),
    /** Image height in pixels (64–2048, multiples of 64). */
    height: z.number().int().min(64).max(2048).default(1024),
    /** Prompt guidance scale (1.0–30.0). Higher = stricter adherence to prompt. */
    scale: z.number().min(1).max(30).default(7),
    /** Number of inference steps (1–100). Higher = more detail but slower. */
    steps: z.number().int().min(1).max(100).default(28),
    /** Seed for reproducible generation. Undefined = random. */
    seed: z.number().int().optional(),
});

export type ImageGenModel = z.infer<typeof ImageGenModelSchema>;
export type ImageGenConfig = z.infer<typeof ImageGenConfigSchema>;
